import * as Yup from 'yup';

export const rangeSchema = (isRequired: boolean) => {
  let schema = Yup.string()
    .trim()
    .test(
      'valid-range',
      'Use the format "min-max" (e.g., 10-50) or a single "0".',
      (value) => {
        if (!value) return !isRequired;

        // Permitir únicamente "0" como valor individual
        if (value === '0') return true;

        // Validar formato min-max
        const match = value.match(/^(\d+)\s*-\s*(\d+)$/);
        if (!match) return false;

        const min = Number(match[1]);
        const max = Number(match[2]);

        if (Number.isNaN(min) || Number.isNaN(max)) return false;
        if (min < 0 || max > 5000) return false;
        if (min > max) return false;

        return true;
      }
    );

  return isRequired
    ? schema.required('Required')
    : schema.notRequired().nullable(true);
};

// UAT-212 P1: parse a capacity value ("5-10", "0", "7", "" / null) into
// numeric {min,max}. Mirrors the backend Experience.get_guest_capacity_limits
// parsing so the two stay consistent.
export const parseRange = (value?: string | number | null): { min: number; max: number } => {
  if (value === undefined || value === null) return { min: 0, max: 0 };
  const v = String(value).trim();
  if (!v || v === '0') return { min: 0, max: 0 };
  const m = v.match(/^(\d+)\s*-\s*(\d+)$/);
  if (m) return { min: Number(m[1]), max: Number(m[2]) };
  if (/^\d+$/.test(v)) {
    const n = Number(v);
    return { min: n, max: n };
  }
  return { min: 0, max: 0 };
};

export type CapacityValues = {
  total_min_guests?: string | number | null;
  total_max_guests?: string | number | null;
  guest_capacity_adults?: string | null;
  guest_capacity_children?: string | null;
  guest_capacity_youth?: string | null;
  guest_capacity_infants?: string | null;
};

// UAT-212 P1: the overall [total_min, total_max] must bracket the aggregated
// per-age range, i.e. sum(per-age mins) <= total_min <= total_max <= sum(per-age
// maxes). Returns an error string, or null when consistent. Pure + testable.
export const validateCapacityCoherence = (v: CapacityValues): string | null => {
  const rawMin = String(v.total_min_guests ?? '').trim();
  const rawMax = String(v.total_max_guests ?? '').trim();
  const totalMin = rawMin === '' ? 0 : Number(rawMin);
  const totalMax = rawMax === '' ? 0 : Number(rawMax);
  if (Number.isNaN(totalMin) || Number.isNaN(totalMax)) return 'Enter numeric guest totals';
  if (totalMax > 0 && totalMin > totalMax) {
    return 'Minimum Guests must be less than Maximum Guests';
  }
  const ages = [
    v.guest_capacity_adults,
    v.guest_capacity_children,
    v.guest_capacity_youth,
    v.guest_capacity_infants,
  ];
  const sumMin = ages.reduce((acc, x) => acc + parseRange(x).min, 0);
  const sumMax = ages.reduce((acc, x) => acc + parseRange(x).max, 0);
  if (sumMax > 0) {
    if (totalMin > 0 && totalMin < sumMin) {
      return 'Minimum Guests is below the sum of the per-age minimums';
    }
    if (totalMax > 0 && totalMax > sumMax) {
      return 'Maximum Guests exceeds the sum of the per-age maximums';
    }
  }
  return null;
};

export function formatPhone(phone: string): string {
  if (!phone) return '';

  // Mantiene solo números y "+"
  let cleaned = phone.replace(/[^\d+]/g, '');

  // Asegura que el "+" solo esté al inicio
  if (cleaned.includes('+')) {
    cleaned = '+' + cleaned.replace(/\+/g, '').replace(/^/, '');
  }

  // Extrae código de país (1–3 dígitos después del +)
  const match = cleaned.match(/^\+?(\d{1,3})(\d{6,14})$/);

  if (!match) return phone;

  const countryCode = match[1];
  const rest = match[2];

  // Divide el resto en bloques legibles (3-3-4 o similar dinámico)
  const parts: string[] = [];
  let temp = rest;

  while (temp.length > 4) {
    parts.push(temp.slice(0, 3));
    temp = temp.slice(3);
  }
  parts.push(temp);

  return `+${countryCode} ${parts.join(' ')}`;
}